'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useAdMesh } from '../hooks/useAdMesh';
import { WeaveFallbackRecommendations } from './WeaveFallbackRecommendations';
import { onStreamingComplete } from '../utils/streamingEvents';
import {
  createInlineExposureTracker,
  type InlineExposureTrackingParams
} from '../utils/inlineExposureTracker';
import { WeaveResponseProcessor } from '../sdk/WeaveResponseProcessor';
import { logger } from '../utils/logger';
import type {
  PlatformResponse,
} from '../types/index';

/**
 * FinalLinkDetectionCheck - Event-driven link detection component
 *
 * This component waits for the 'admesh:streamingComplete' event from ChatWindow
 * before performing final link detection. This eliminates race conditions where
 * timeout-based detection would trigger before streaming completes.
 */
interface FinalLinkDetectionCheckProps {
  containerId: string;
  messageId: string;
  sessionId: string;
  sdk: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  query?: string;
  recommendations?: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  onLinksFound: (count: number) => void;
  onNoLinksFound: () => void;
  children: React.ReactNode;
}

const FinalLinkDetectionCheck: React.FC<FinalLinkDetectionCheckProps> = ({
  containerId,
  messageId,
  sessionId,
  sdk,
  query,
  recommendations = [],
  onLinksFound,
  onNoLinksFound,
  children
}) => {
  const [checkComplete, setCheckComplete] = useState(false);
  const [waitingForStreamEnd, setWaitingForStreamEnd] = useState(true);
  const [linksFound, setLinksFound] = useState(false);
  const exposureTrackerRef = useRef(createInlineExposureTracker());

  // Use refs to avoid recreating the callback on every render
  const onLinksFoundRef = useRef(onLinksFound);
  const onNoLinksFoundRef = useRef(onNoLinksFound);
  const containerIdRef = useRef(containerId);
  const sdkRef = useRef(sdk);
  const sessionIdRef = useRef(sessionId);
  const queryRef = useRef(query);

  // Update refs when values change
  useEffect(() => {
    onLinksFoundRef.current = onLinksFound;
    onNoLinksFoundRef.current = onNoLinksFound;
    containerIdRef.current = containerId;
    sdkRef.current = sdk;
    sessionIdRef.current = sessionId;
    queryRef.current = query;
  }, [onLinksFound, onNoLinksFound, containerId, sdk, sessionId, query]);

  useEffect(() => {
    return () => {
      exposureTrackerRef.current.cleanup();
    };
  }, []);

  const trackExposurePixel = useCallback(
    ({ exposureUrl, recommendationId, linkElement }: InlineExposureTrackingParams) => {
      exposureTrackerRef.current.startTracking({
        exposureUrl,
        recommendationId,
        linkElement,
        sessionId: sessionIdRef.current,
        logPrefix: '[FinalLinkDetectionCheck]'
      });
    },
    []
  );

  const performFinalCheck = useCallback(() => {
    logger.log('[FinalLinkDetectionCheck] 🔍 Performing final link detection...');

    try {
      const container = document.getElementById(containerIdRef.current);
      if (!container) {
        logger.warn('[FinalLinkDetectionCheck] ❌ Container not found');
        setCheckComplete(true);
        onNoLinksFound();
        return;
      }

      // Create WeaveResponseProcessor instance
      const weaveProcessor = new WeaveResponseProcessor({
        autoAddLabels: true,
        fireExposurePixels: true
      });

      // Use fetched recommendations if available, otherwise fall back to pattern-based detection
      const recsToUse = recommendations.length > 0 ? recommendations : [];
      logger.log(`[FinalLinkDetectionCheck] 📋 Using ${recsToUse.length} recommendations for link detection`);
      
      // Development logging: Log recommendation details used for link detection
      if (recsToUse.length > 0 && process.env.NODE_ENV === 'development') {
        recsToUse.forEach((rec, index) => {
          logger.log(`[FinalLinkDetectionCheck] 📋 Recommendation ${index + 1} (DEV):`, {
            recommendation_id: rec?.recommendation_id,
            click_url:
              rec?.tracking?.click_url ||
              rec?.recommendation?.creative?.landing_page_url,
            format: rec?.recommendation?.format,
            brand_name: rec.render?.creative?.advertiser?.brand_name,
            cta_url: rec.render?.creative?.landing_page_url,
          });
        });
      }

      // Scan for AdMesh links one final time
      const detectedLinks = weaveProcessor.scanAndProcessLinks(
        container,
        recsToUse, // Use fetched recommendations for brand URL matching
        ({ exposureUrl, recommendationId, linkElement }: InlineExposureTrackingParams) =>
          trackExposurePixel({ exposureUrl, recommendationId, linkElement })
      );

      logger.log(`[FinalLinkDetectionCheck] 📊 Final check result: ${detectedLinks.length} links`);

      if (detectedLinks.length > 0) {
        // Links found! Cancel fallback
        logger.log('[FinalLinkDetectionCheck] ✅ Links detected - canceling fallback (no API call)');
        setLinksFound(true);
        onLinksFoundRef.current(detectedLinks.length);
      } else {
        // No links found, proceed with fallback
        logger.log(`[FinalLinkDetectionCheck] ⚠️ No links found - rendering fallback (will use ${recsToUse.length} previous recommendation(s) if available)`);
        setLinksFound(false);
        onNoLinksFoundRef.current();
      }

      setCheckComplete(true);
    } catch (error) {
      logger.error('[FinalLinkDetectionCheck] ❌ Error during final check');
      setCheckComplete(true);
      onNoLinksFoundRef.current();
    }
  }, [trackExposurePixel, recommendations]);

  useEffect(() => {
    logger.log('[FinalLinkDetectionCheck] ⏳ Setting up listener', {
      messageId,
      sessionId,
      recommendationsCount: recommendations.length
    });

    let timeoutId: NodeJS.Timeout | null = null;
    let eventReceived = false;

    // Listen for the streamingComplete event from ChatWindow
    const cleanup = onStreamingComplete(messageId, sessionId, (detail) => {
      logger.log('[FinalLinkDetectionCheck] 🎯 Received streamingComplete event', {
        messageId: detail.messageId,
        sessionId: detail.sessionId,
        timestamp: detail.timestamp
      });
      eventReceived = true;
      
      // Clear timeout since event was received
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      setWaitingForStreamEnd(false);

      // Small delay to ensure DOM is fully updated
      setTimeout(() => {
        performFinalCheck();
      }, 100);
    });

    // Fallback timeout: If streamingComplete doesn't fire within 5 seconds, trigger check anyway
    // This ensures fallback recommendations display even if event system fails
    timeoutId = setTimeout(() => {
      if (!eventReceived) {
        logger.warn('[FinalLinkDetectionCheck] ⏱️ Timeout: streamingComplete event not received, triggering fallback check anyway');
        setWaitingForStreamEnd(false);
        setTimeout(() => {
          performFinalCheck();
        }, 100);
      }
    }, 5000); // 5 second timeout

    return () => {
      logger.log('[FinalLinkDetectionCheck] 🧹 Cleaning up listener');
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, sessionId]); // performFinalCheck depends on recommendations prop, but recommendations should be stable for a given messageId

  // Show loading state while waiting for stream to complete
  if (waitingForStreamEnd) {
    return <></>;
  }

  // Show checking state while performing final detection
  if (!checkComplete) {
    return <></>;
  }

  // Only render children (fallback) if NO links were found
  if (linksFound) {
    logger.log('[FinalLinkDetectionCheck] 🚫 Links found - NOT rendering fallback');
    return <></>;
  }

  logger.log('[FinalLinkDetectionCheck] ✅ No links found - rendering fallback');
  return <>{children}</>;
};

export interface WeaveAdFormatContainerProps {
  /** Unique ID for this message container */
  messageId: string;

  /** The LLM response content (may contain AdMesh links) */
  children: React.ReactNode;

  /** Fallback format if no links detected (default: 'tail') */
  fallbackFormat?: 'product' | 'tail';

  /** Optional callback when AdMesh links are detected */
  onLinksDetected?: (count: number) => void;

  /** Optional callback when no links are detected (fallback should be rendered) */
  onNoLinksDetected?: () => void;

  /** Optional callback on error */
  onError?: (error: Error) => void;

  /** Optional CSS class for the container */
  className?: string;

  /** Optional query text for fallback API calls (used when no links are detected) */
  query?: string;

  /** Optional callback to notify parent when fallback state changes */
  onFallbackChange?: (shouldFallback: boolean) => void;

  /** Optional callback when weave injection is attempted */
  onWeaveAttempt?: (messageId: string) => void;

  /** Optional callback when weave injection outcome is determined */
  onWeaveOutcome?: (messageId: string, success: boolean, reason?: string) => void;

  /** Optional container ID for follow-up suggestions */
  followups_container_id?: string;

  /** Callback to execute query when follow-up is selected (required for follow-up functionality) */
  onExecuteQuery?: (query: string) => void | Promise<void>;

  /** Optional callback when a sponsored followup is detected */
  onFollowupDetected?: (
    followupSuggestion: any, // Will be updated to proper type later
    recommendationId: string,
  ) => void;

  /** Signal indicating if the followup container is ready in the DOM */
  isContainerReady?: boolean;
}

/**
 * WeaveAdFormatContainer - Automatic Weave Ad Format handling component
 *
 * Wraps LLM response content and automatically:
 * - Detects AdMesh links in the content
 * - Fires exposure tracking for detected links
 * - Adds Ad labels to links
 * - Shows "Why this ad?" tooltip on hover
 * - Provides context for WeaveFallbackRecommendations component
 *
 * @example
 * ```tsx
 * <WeaveAdFormatContainer
 *   messageId={message.id}
 *   query={userQuery}
 *   onLinksDetected={(count) => console.log(`Found ${count} ads`)}
 * >
 *   {llmResponseContent}
 * </WeaveAdFormatContainer>
 * ```
 */
export const WeaveAdFormatContainer: React.FC<WeaveAdFormatContainerProps> = ({
  messageId,
  children,
  fallbackFormat = 'tail',
  onLinksDetected,
  onNoLinksDetected,
  onError,
  className,
  query,
  onFallbackChange,
  onWeaveAttempt,
  onWeaveOutcome
}) => {
  const { sessionId, sdk, language, location, userId, model, messages } = useAdMesh();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerId = `weave-ad-container-${messageId}`;
  const exposureTrackerRef = useRef(createInlineExposureTracker());
  const scannedRef = useRef(false);
  const weaveProcessorRef = useRef<WeaveResponseProcessor | null>(null);
  const [recommendations, setRecommendations] = useState<PlatformResponse[]>([]);
  const recommendationsRef = useRef<PlatformResponse[]>([]);

  // Initialize WeaveResponseProcessor once
  useEffect(() => {
    if (!weaveProcessorRef.current) {
      weaveProcessorRef.current = new WeaveResponseProcessor({
        autoAddLabels: true,
        fireExposurePixels: true
      });
    }
    return () => {
      if (weaveProcessorRef.current) {
        weaveProcessorRef.current.stopWatching();
        weaveProcessorRef.current.clearCache();
      }
    };
  }, []);

  // Cleanup exposure tracker on unmount
  useEffect(() => {
    return () => {
      exposureTrackerRef.current.cleanup();
    };
  }, []);

  // Fetch recommendations from SDK cache if available
  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!sdk || !query?.trim() || !messageId) {
        logger.log('[WeaveAdFormatContainer] ⚠️ Missing SDK, query, or messageId - using pattern-based detection');
        return;
      }

      try {
        logger.log('[WeaveAdFormatContainer] 📤 Fetching recommendations from SDK for brand URL matching');
        // Use context values (from AdMeshProvider) for canonical AIP PlatformRequest fields
        const platformResponse = await sdk.fetchPlatformResponse({
          query: query.trim(),
          sessionId: sessionId,
          messageId: messageId,
          language: language,
          location: location,
          userId: userId,
          model: model,
          messages: messages,
        });
        const recs = platformResponse?.status === 'generated' ? [platformResponse] : [];
        setRecommendations(recs);
        recommendationsRef.current = recs; // Update ref to avoid dependency issues
        logger.log('[WeaveAdFormatContainer] 📥 Got platform response:', {
          status: platformResponse?.status,
          recommendationId: platformResponse?.recommendation_id,
          hasRecommendation: !!platformResponse?.recommendation,
          format: platformResponse?.recommendation?.format
        });
        
        if (platformResponse?.recommendation) {
          const creative = platformResponse.recommendation.creative;
          const brandUrl = creative.landing_page_url;
          logger.log('[WeaveAdFormatContainer] 🎯 Found creative in platform response:', {
            brandName: creative.brand_name,
            headline: creative.headline,
            description: creative.description,
            brandUrl: brandUrl
          });
        } else {
          logger.log('[WeaveAdFormatContainer] ⚠️ No recommendation found in platform response');
        }
      } catch (error) {
        logger.warn('[WeaveAdFormatContainer] ⚠️ Failed to fetch recommendations, falling back to pattern-based detection:', error);
        setRecommendations([]);
      }
    };

    fetchRecommendations();
  }, [sdk, sessionId, messageId, query]);

  // Scan for links immediately when container is ready and on children changes
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !weaveProcessorRef.current) {
      return;
    }

    const weaveProcessor = weaveProcessorRef.current;

    const trackExposurePixel = ({ exposureUrl, recommendationId, linkElement }: InlineExposureTrackingParams) => {
      exposureTrackerRef.current.startTracking({
        exposureUrl,
        recommendationId,
        linkElement,
        sessionId,
        logPrefix: '[WeaveAdFormatContainer]'
      });
    };

    // Scan immediately
    const scanLinks = () => {
      // Emit weave attempt signal
      logger.log('[WeaveAdFormatContainer] 🔍 Weave injection attempt started');
      onWeaveAttempt?.(messageId);

      // Use fetched recommendations if available, otherwise fall back to pattern-based detection
      // Use ref to avoid triggering re-runs when recommendations change
      const recsToUse = recommendationsRef.current.length > 0 ? recommendationsRef.current : [];
      logger.log(`[WeaveAdFormatContainer] 📋 Using ${recsToUse.length} recommendations for link detection`);

      try {
        const detectedLinks = weaveProcessor.scanAndProcessLinks(
          container,
          recsToUse, // Use fetched recommendations for brand URL matching
          trackExposurePixel
        );

        if (detectedLinks.length > 0 && !scannedRef.current) {
          scannedRef.current = true;
          logger.log(`[WeaveAdFormatContainer] ✅ Weave injection succeeded: ${detectedLinks.length} links detected`);
          onWeaveOutcome?.(messageId, true, `Found ${detectedLinks.length} links`);
          onLinksDetected?.(detectedLinks.length);
          onFallbackChange?.(false);
        } else if (!scannedRef.current) {
          // No links found on first scan - will be handled by FinalLinkDetectionCheck
          // But we emit the outcome signal here for immediate feedback
          logger.log('[WeaveAdFormatContainer] ⚠️ Weave injection: no links found on initial scan');
          // Don't emit outcome yet - wait for FinalLinkDetectionCheck to confirm
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        logger.error(`[WeaveAdFormatContainer] ❌ Weave injection failed: ${errorMessage}`);
        onWeaveOutcome?.(messageId, false, errorMessage);
        // Immediately trigger fallback on error
        onNoLinksDetected?.();
        onFallbackChange?.(true);
      }
    };

    // Initial scan after a small delay to ensure DOM is ready
    const timeoutId = setTimeout(scanLinks, 100);

    // Watch for new links (streaming support)
    // Use ref to avoid triggering re-runs when recommendations change
    weaveProcessor.watchForNewLinks(container, recommendationsRef.current, trackExposurePixel);

    return () => {
      clearTimeout(timeoutId);
      weaveProcessor.stopWatching();
    };
  }, [children, sessionId, messageId, onLinksDetected, onFallbackChange, onWeaveAttempt, onWeaveOutcome]); // Removed recommendations from deps to prevent infinite loop

  const preferredFormat = recommendations[0]?.recommendation?.format;
  const isWeaveFormat = preferredFormat === 'weave';

  // Log when format is weave (for debugging)
  useEffect(() => {
    if (query && isWeaveFormat) {
      logger.log(`[WeaveAdFormatContainer] 🎯 Format is "weave" (preferred: ${preferredFormat}) - skipping fallback recommendations`);
    }
  }, [query, isWeaveFormat, preferredFormat]);

  return (
    <>
      <div
        ref={containerRef}
        id={containerId}
        className={className}
        data-weave-ad-format="true"
        data-message-id={messageId}
      >
        {children}
      </div>

      {/* Event-driven link detection and fallback rendering - only show if format is NOT weave */}
      {query && !isWeaveFormat && (
        <FinalLinkDetectionCheck
          containerId={containerId}
          messageId={messageId}
          sessionId={sessionId}
          sdk={sdk}
          query={query}
          recommendations={recommendations}
          onLinksFound={(count) => {
            logger.log(`[WeaveAdFormatContainer] ✅ Links found (${count}) - no fallback needed`);
            // Emit weave outcome success if not already emitted
            onWeaveOutcome?.(messageId, true, `Found ${count} links in final check`);
            onLinksDetected?.(count);
            onFallbackChange?.(false);
          }}
          onNoLinksFound={() => {
            logger.log(`[WeaveAdFormatContainer] ⚠️ No links found - rendering fallback`);
            logger.log(`[WeaveAdFormatContainer] 📋 Passing ${recommendations.length} recommendation(s) to fallback component`);
            // Emit weave outcome failure
            onWeaveOutcome?.(messageId, false, 'No links detected in final check');
            onNoLinksDetected?.();
            onFallbackChange?.(true);
          }}
        >
          <WeaveFallbackRecommendations
            format={fallbackFormat}
            messageId={messageId}
            query={query}
            fallback={true}
            onError={onError}
            previousRecommendations={recommendations.length > 0 ? recommendations : undefined}
          />
        </FinalLinkDetectionCheck>
      )}

      {/* Render follow-up portal if follow-up exists */}
    </>
  );
};

export default WeaveAdFormatContainer;
